import type { Listing, StatePatch, ShowMode } from '../types';
import { api } from '../api';
import { patchItem } from './feed';
import { pushToast } from './toasts';
import { brandLabel } from '../format';

export interface Derived {
  seen: boolean;
  saved: boolean;
  hidden: boolean;
}

export type TriageAction = 'save' | 'hide' | 'open' | 'view';

export function derive(l: Pick<Listing, 'seen_at' | 'saved_at' | 'hidden_at'>): Derived {
  return { seen: l.seen_at !== null, saved: l.saved_at !== null, hidden: l.hidden_at !== null };
}

/** Pure: the server patch an action implies, or null when it is a no-op. */
export function actionPatch(d: Derived, action: TriageAction): StatePatch | null {
  switch (action) {
    case 'save':
      return d.saved ? { saved: false } : { saved: true, seen: true };
    case 'hide':
      return { hidden: true };
    case 'open':
    case 'view':
      return d.seen ? null : { seen: true };
  }
}

/** Pure: the optimistic timestamp changes a patch implies. */
export function applyPatch(_l: Listing, patch: StatePatch, now: string): Partial<Listing> {
  const out: Partial<Listing> = {};
  if (patch.seen !== undefined) out.seen_at = patch.seen ? now : null;
  if (patch.saved !== undefined) out.saved_at = patch.saved ? now : null;
  if (patch.hidden !== undefined) out.hidden_at = patch.hidden ? now : null;
  if (patch.note !== undefined) out.note = patch.note;
  return out;
}

/** Pure: prototype visible() minus the server-side filters (tier/exclude/brand/price). */
export function clientVisible(d: Derived, show: ShowMode): boolean {
  if (d.hidden) return false;
  if (show === 'saved') return d.saved;
  if (show === 'unseen') return !d.seen || d.saved;
  return true;
}

type Snapshot = Pick<Listing, 'seen_at' | 'saved_at' | 'hidden_at' | 'note'>;
const snapshot = (l: Listing): Snapshot => ({
  seen_at: l.seen_at, saved_at: l.saved_at, hidden_at: l.hidden_at, note: l.note,
});

export async function runAction(l: Listing, action: TriageAction): Promise<void> {
  if (action === 'open') window.open(l.url, '_blank', 'noopener');

  const patch = actionPatch(derive(l), action);
  if (!patch) return;

  const prev = snapshot(l);
  const now = new Date().toISOString();
  patchItem(l.id, applyPatch(l, patch, now));

  if (action === 'hide') {
    pushToast(`Đã ẩn "${brandLabel(l)}".`, {
      label: 'Hoàn tác',
      ms: 5000,
      fn: () => void undoHide(l.id, prev),
    });
  }

  try {
    await api.setState(l.id, patch);
  } catch (e) {
    patchItem(l.id, prev);
    pushToast(`Không lưu được thay đổi: ${(e as Error).message}`, { ms: 4000 });
  }
}

async function undoHide(id: number, prev: Snapshot): Promise<void> {
  patchItem(id, prev);
  try {
    await api.setState(id, { hidden: false });
  } catch (e) {
    pushToast(`Hoàn tác không lưu được: ${(e as Error).message}`, { ms: 4000 });
  }
}
